/**
 * Where Maya writes: the prototype's composer, on a computer and an iPad or as the phone's docked bar. Behind the "+"
 * are her files, the Assets picker and her notes; the list button asks a question with choices. What it does is in
 * `useComposer`.
 */
import type { ReactNode } from "react";
import { Button } from "../components/ui/Button";
import { Face } from "../components/ui/Face";
import { Icon } from "../components/ui/Icon";
import { Menu } from "../components/ui/Menu";
import { PendingCard, VideoNoteDialog, VoiceRec } from "./ComposerParts";
import { type Outgoing, useComposer } from "./useComposer";

export interface ComposerProps {
  /** Who it goes to: "everyone in the room", "the circle", "Joe". */
  to: string;
  phone: boolean;
  host: { name: string; photo: string | null };
  send: string;
  sendIcon?: "send" | "mail";
  /** The words in the box before anything is typed. */
  placeholder: string;
  away?: boolean;
  /** The ticks that sit before the button: Also email it, Pin it. */
  checks?: ReactNode;
  /** A phone shows them in a row above the bar. */
  onAsk?: () => void;
  /** The question goes in the + menu instead of beside it. */
  askInPlus?: boolean;
  draft?: () => Promise<string>;
  onSend: (out: Outgoing) => Promise<void>;
  onTyping?: () => void;
  /** Why the last send did not go, in the server's words. */
  notice?: string | null;
  /** What the line being answered says, with a way to let it go. */
  replying?: { name: string; text: string; onClear: () => void } | null;
  /** The row above the box, for Direct's Chat or Email switch. */
  head?: ReactNode;
  /** An email: the From and To rows, a subject and a tall body, in place of one line; a phone opens it full screen. */
  mail?: { from: string; fromEmail: string | null; to: { name: string; photo: string | null }; subject: string; title: string; onCancel: () => void };
}

export function Composer({ to, phone, host, send, sendIcon = "send", placeholder, away = false, checks, onAsk, askInPlus = false, draft, onSend, onTyping, notice, replying, head, mail }: ComposerProps) {
  const c = useComposer({ to, away, draft, ask: askInPlus ? onAsk : undefined, onSend, email: !!mail, subject: mail?.subject });
  const { recorder } = c;
  const video = c.videoOpen ? (
    <VideoNoteDialog
      layout={phone ? "phone" : undefined}
      onClose={() => c.setVideoOpen(false)}
      onUse={(blob, seconds, live) => {
        c.setVideoOpen(false);
        c.sendTake({ kind: "video", blob, seconds, preview: URL.createObjectURL(blob), live });
      }}
    />
  ) : null;
  const above = (
    <>
      {replying ? (
        <div className="cmp-reply">
          <Icon name="chat" />
          <span>
            Replying to <b>{replying.name}</b>: {replying.text}
          </span>
          <button type="button" aria-label="Do not reply to this line" onClick={replying.onClear}>
            <Icon name="x" />
          </button>
        </div>
      ) : null}
      {recorder.rec ? <VoiceRec seconds={recorder.rec.seconds} recorder={recorder} /> : null}
      {c.pending ? (
        <div className="catt">
          <PendingCard pending={c.pending} host={host} onDrop={c.dropPending} />
        </div>
      ) : null}
    </>
  );
  const plus = (
    <Menu label="Add" icon="plus" align="left" buttonClassName={phone ? "hp-cmp-add" : "cmp-tool"} menuClassName="cmenu" items={c.items} />
  );
  const ask = onAsk && !askInPlus ? (
    <button type="button" className={phone ? "hp-cmp-add" : "cmp-tool"} title="Ask a question with choices" aria-label="Ask a question with choices" onClick={onAsk}>
      <Icon name="list" />
    </button>
  ) : null;
  const note = notice ? (
    <p className="cmp-note" role="status">
      {notice}
    </p>
  ) : null;

  const form = mail ? (
    <div className="dc-mail">
      <div className="dc-row">
        <span>From</span>
        <b>{mail.from}</b>
        {!phone && mail.fromEmail ? <small>{mail.fromEmail}</small> : null}
      </div>
      <div className="dc-row">
        <span>To</span>
        <b className="dc-to">
          <Face name={mail.to.name} photo={mail.to.photo} size={22} />
          {mail.to.name}
        </b>
      </div>
      <label className="dc-row">
        <span>Subject</span>
        <input placeholder="What is it about?" value={c.subject} onChange={(e) => c.setSubject(e.target.value)} />
      </label>
      <textarea ref={c.box} className="dc-body" aria-label={placeholder} placeholder={placeholder} value={c.text} onChange={(e) => c.setText(e.target.value)} onKeyDown={c.onKeyDown} />
    </div>
  ) : null;

  if (phone && mail) {
    return (
      <div className="hp-compose" role="dialog" aria-label={mail.title}>
        <header className="hp-nav">
          <button type="button" className="hp-back" onClick={mail.onCancel}>
            Cancel
          </button>
          <span className="hp-nav-t">
            <b>{mail.title}</b>
          </span>
          <span className="hp-nav-r">
            <button type="button" className="hp-golive" disabled={c.sending} onClick={() => void c.send()}>
              <Icon name="mail" /> Send
            </button>
          </span>
        </header>
        {form}
        {above}
        {note}
        <div className="hp-compose-b">
          {plus}
          {mail.fromEmail ? <small>From {mail.fromEmail}</small> : null}
        </div>
        {video}
      </div>
    );
  }

  if (phone) {
    return (
      <div className="hp-cmpw">
        {head || checks ? (
          <div className="hp-cmp-top">
            {head}
            {checks}
          </div>
        ) : null}
        {above}
        {note}
        <div className="hp-cmp">
          {plus}
          {ask}
          <input
            className="hp-cmp-in"
            aria-label={placeholder}
            placeholder={placeholder}
            value={c.text}
            onChange={(e) => {
              c.setText(e.target.value);
              onTyping?.();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void c.send();
              }
            }}
          />
          <button type="button" className="hp-cmp-go" aria-label={send} disabled={c.sending} onClick={() => void c.send()}>
            <Icon name={sendIcon} />
          </button>
        </div>
        {video}
      </div>
    );
  }

  return (
    <div className={mail ? "cmp mail" : "cmp"}>
      {head ? <div className="dc-top">{head}</div> : null}
      {above}
      {form ?? (
        <textarea
          ref={c.box}
          className="cmp-in"
          rows={1}
          aria-label={placeholder}
          placeholder={placeholder}
          value={c.text}
          onChange={(e) => {
            c.setText(e.target.value);
            c.grow();
            onTyping?.();
          }}
          onKeyDown={c.onKeyDown}
        />
      )}
      {note}
      <div className="cmp-b">
        {plus}
        {ask}
        <span className="cmp-sp" />
        {checks}
        <Button variant="p" icon={mail ? "mail" : sendIcon} size="s" onClick={() => void c.send()} disabled={c.sending}>
          {send}
        </Button>
      </div>
      {video}
    </div>
  );
}
